import React, { memo, useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';

import {
  AddDocument,
  Document,
} from '@johnlewispartnership/wtr-style-dictionary/icons/react/Documents';
import { MyWaitroseCard } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Cards';
import { Account } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Account';
import { GridView } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Controls';
import { Delivery } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Slots';

import AnchorLink from 'components/AnchorLink';

import { urls } from 'constants/urls';
import { useDeliveryPassStatus } from 'contexts/DeliveryPass';
import SignOutButton from 'components/AuthenticationAction/SignOut/Button';
import useOnClickOutside from 'hooks/use-on-click-outside';
import styles from './MyAccount.scss';

export const DeliveryPassStatusEnum = Object.freeze({
  AVAILABLE: 'AVAILABLE', // User does not have a delivery pass, but it is available for them to purchase
  ACTIVE: 'ACTIVE', // User has an active delivery pass
  EXPIRED: 'EXPIRED',
  PENDING: 'PENDING',
});

export const myAccountLabels = {
  myWaitroseLoyalty: 'My Waitrose loyalty',
  lists: 'Lists',
  accountOverview: 'Account overview',
  myOrders: 'My orders',
  signOut: 'Sign out',
  deliveryPass: 'Delivery Pass',
};

const logAnalytics = (button, trackEvent) => {
  trackEvent({
    event: 'account_menu_click',
    my_account_menu: {
      button,
    },
    temporary: true,
  });
};

const MyAccount = memo(() => {
  const [hidden, setHidden] = useState(true);
  const insideRef = useRef();

  const deliveryPassStatus = useDeliveryPassStatus();

  const { trackEvent } = useAnalytics();

  const pushMenuClick = useCallback(
    (buttonLabel, additionalData = {}) => {
      trackEvent({
        event: 'click_megamenu',
        mega_menu: {
          button_clicked: buttonLabel,
          ...additionalData,
        },
      });
    },
    [trackEvent],
  );

  const toggleVisibility = useCallback(() => {
    setHidden(!hidden);
  }, [hidden]);

  const handleButtonClick = useCallback(() => {
    if (hidden && trackEvent) {
      logAnalytics('Header', trackEvent);
    }
    toggleVisibility();
  }, [hidden, toggleVisibility, trackEvent]);

  useOnClickOutside(insideRef, () => {
    if (!hidden) {
      setHidden(true);
    }
  });

  const handleBlur = useCallback(() => {
    if (!hidden) {
      toggleVisibility();
    }
  }, [hidden, toggleVisibility]);

  const handleFocus = useCallback(() => {
    if (hidden) {
      toggleVisibility(false);
    }
  }, [hidden, toggleVisibility]);

  const handleLinkClick = useCallback(
    event => {
      const button = event.target.textContent;

      toggleVisibility();
      pushMenuClick(`My Account: ${button}`);
      logAnalytics(button, trackEvent);
    },
    [pushMenuClick, toggleVisibility, trackEvent],
  );

  return (
    <nav
      aria-labelledby="myAccountAuthenticated"
      ref={insideRef}
      className={styles.accountActionWrapper}
      id="authenticated"
      data-testid="my-account-menu"
    >
      <button
        aria-controls="account-actions"
        aria-expanded={!hidden}
        className={classNames(styles.button)}
        data-testid="my-account-button"
        id="account-details"
        onClick={handleButtonClick}
        tabIndex={-1}
        type="button"
      >
        <Account className={styles.buttonIcon} size="small" />
        <span id="myAccountAuthenticated" className={styles.btnText}>
          Account
        </span>
      </button>
      <div className={styles.accountPopupWrapper}>
        <div
          data-testid="account-popup"
          className={classNames(styles.accountPopup, { [styles.srOnly]: hidden })}
        >
          <ul className={styles.accountActions} id="account-actions">
            <li>
              <AnchorLink
                data-testid="account-overview-link"
                id="my-details"
                onBlur={handleBlur}
                onClick={handleLinkClick}
                onFocus={handleFocus}
                to={urls.myAccount}
              >
                <GridView className={styles.accountMenuIcon} size="medium" />
                {myAccountLabels.accountOverview}
              </AnchorLink>
            </li>
            <li>
              <AnchorLink
                data-testid="my-orders-link"
                id="my-details"
                onBlur={handleBlur}
                onClick={handleLinkClick}
                onFocus={handleFocus}
                to={urls.myOrdersPage}
                underline="hover"
              >
                <Document className={styles.accountMenuIcon} size="medium" />
                {myAccountLabels.myOrders}
              </AnchorLink>
            </li>
            <li>
              <AnchorLink
                data-testid="lists-link"
                onBlur={handleBlur}
                onClick={handleLinkClick}
                onFocus={handleFocus}
                to={urls.lists}
              >
                <AddDocument className={styles.accountMenuIcon} size="medium" />
                {myAccountLabels.lists}
              </AnchorLink>
            </li>
            {[DeliveryPassStatusEnum.ACTIVE, DeliveryPassStatusEnum.AVAILABLE].includes(
              deliveryPassStatus,
            ) ? (
              <li>
                <AnchorLink
                  data-testid="deliveryPass-link"
                  onBlur={handleBlur}
                  onClick={handleLinkClick}
                  onFocus={handleFocus}
                  to={
                    deliveryPassStatus === DeliveryPassStatusEnum.ACTIVE
                      ? urls.deliveryPassActive
                      : urls.deliveryPassAvailable
                  }
                  underline="hover"
                >
                  <Delivery className={styles.accountMenuIcon} size="medium" />
                  {myAccountLabels.deliveryPass}
                </AnchorLink>
              </li>
            ) : null}
            <li>
              <AnchorLink
                data-testid="my-waitrose-link"
                onBlur={handleBlur}
                onClick={handleLinkClick}
                onFocus={handleFocus}
                underline="hover"
                to={urls.myWaitrosePage}
              >
                <MyWaitroseCard className={styles.accountMenuIcon} size="medium" />
                {myAccountLabels.myWaitroseLoyalty}
              </AnchorLink>
            </li>
            <li className={styles.signOut}>
              <SignOutButton
                data-testid="account-sign-out"
                className={styles.signOutButton}
                onClick={handleLinkClick}
                onBlur={handleBlur}
                onFocus={handleFocus}
                width="full"
              />
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
});

MyAccount.displayName = 'MyAccount';

export default MyAccount;
